import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.background');
import { OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Service } from "src/libs/portal/season/service";
import {
    cellyticsAnalysisDataUrl,
    cellyticsAnalysisSet,
    cellyticsCurrentAnalysisSetId,
    cellyticsDefaultAnalysisSet,
} from '../cellytics-analysis';

const ACCESS_SESSION_KEY = "cellytics.access";
const ACCESS_SESSION_VALUE = "granted";
const ACCESS_USER = {
    id: "cellytics-access",
    email: "",
    name: "Cellytics NK",
    role: "admin",
};

@Component({
    selector: 'wiz-page-background',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.background/view.scss */
/* src/app/page.background/view.scss: no such file or directory */`],
})
export class PageBackgroundComponent implements OnInit, OnDestroy {
    public analysis: any = null;
    public analysisSetId: string = "";
    public analysisSet: any = null;
    public loading: boolean = true;
    public error: string = "";
    public selectedSampleId: string = "";
    public imageModal: any = null;

    public comparisonRows: any[] = [
        { key: "finalCount", label: "최종 cell", digits: 0, source: "case" },
        { key: "overlapRejected", label: "Overlap 제외", digits: 0, source: "case" },
        { key: "CMV", label: "CMV", digits: 2, source: "metrics" },
        { key: "PPD", label: "PPD", digits: 2, source: "metrics" },
        { key: "MMD", label: "MMD", digits: 2, source: "metrics" },
        { key: "MMD_SD", label: "MMD_STD", digits: 2, source: "metrics" },
        { key: "WCM", label: "WCM", digits: 2, source: "metrics" },
        { key: "WCM_SD", label: "WCM_STD", digits: 2, source: "metrics" },
        { key: "WSM", label: "WSM", digits: 2, source: "metrics" },
        { key: "WSM_SD", label: "WSM_STD", digits: 2, source: "metrics" },
        { key: "CSP", label: "CSP", digits: 2, source: "metrics" },
    ];

    private routerSub: any = null;

    constructor(@Inject( Service) public service: Service,@Inject( Router)  private router: Router) { }

    public async ngOnInit() {
        await this.service.init();
        if (!this.hasAccess()) {
            location.href = "/access";
            return;
        }
        this.applyAccessSession();
        this.syncAnalysisSet();
        this.bindRouteChanges();
        await this.load();
    }

    public ngOnDestroy() {
        if (this.routerSub) this.routerSub.unsubscribe();
    }

    private hasAccess() {
        return this.service.auth.check() || window.sessionStorage.getItem(ACCESS_SESSION_KEY) === ACCESS_SESSION_VALUE;
    }

    private applyAccessSession() {
        if (this.service.auth.check()) return;
        this.service.auth.status = true;
        this.service.auth.session = ACCESS_USER;
    }

    private syncAnalysisSet() {
        this.analysisSetId = cellyticsCurrentAnalysisSetId();
        this.analysisSet = cellyticsAnalysisSet(this.analysisSetId) || cellyticsDefaultAnalysisSet();
    }

    private bindRouteChanges() {
        if (this.routerSub) return;
        this.routerSub = this.router.events.subscribe(async (event: any) => {
            if (!(event instanceof NavigationEnd)) return;
            const nextSetId = cellyticsCurrentAnalysisSetId();
            if (nextSetId === this.analysisSetId) return;
            this.syncAnalysisSet();
            await this.load();
        });
    }

    public async load() {
        this.syncAnalysisSet();
        this.loading = true;
        this.error = "";
        await this.service.render();

        try {
            const response = await fetch(cellyticsAnalysisDataUrl(this.analysisSetId), { cache: "no-cache" });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            this.analysis = await response.json();
            this.selectedSampleId = this.samples[0]?.id || "";
        } catch (error) {
            this.error = "분석 데이터를 불러오지 못했습니다.";
        }

        this.loading = false;
        await this.service.render();
    }

    public get samples() {
        return this.analysis?.samples || [];
    }

    public get selectedSample() {
        return this.samples.find((sample: any) => sample.id === this.selectedSampleId) || this.samples[0] || null;
    }

    private isTrustedNative(sample: any = this.selectedSample) {
        return sample?.nativeVerified === true;
    }

    public get originalCase() {
        return this.sampleCase(this.selectedSample, "original");
    }

    public get backgroundCase() {
        return this.sampleCase(this.selectedSample, "background");
    }

    public get selectedBackgroundVisual() {
        return this.selectedSample?.visuals?.backgroundHeatmap || null;
    }

    public get backgroundRows() {
        return this.samples
            .map((sample: any) => {
                const original = this.sampleCase(sample, "original");
                const background = this.sampleCase(sample, "background");
                const visual = sample.visuals?.backgroundHeatmap || null;
                if (!original || !background || !visual) return null;
                return {
                    sample,
                    original,
                    background,
                    visual,
                    countDelta: background.finalCount - original.finalCount,
                    mmdSdDelta: this.metricValue(background, "MMD_SD") - this.metricValue(original, "MMD_SD"),
                    cspDelta: this.metricValue(background, "CSP") - this.metricValue(original, "CSP"),
                };
            })
            .filter((row: any) => !!row);
    }

    public get backgroundAverage() {
        const rows = this.backgroundRows;
        const countDeltas = rows.map((row: any) => row.countDelta);
        const mmdSdDeltas = rows.map((row: any) => row.mmdSdDelta).filter((value: any) => !Number.isNaN(Number(value)));
        const cspDeltas = rows.map((row: any) => row.cspDelta).filter((value: any) => !Number.isNaN(Number(value)));
        return {
            sampleCount: rows.length,
            avgCvReduction: this.average(rows.map((row: any) => row.visual.cvReductionPercent)),
            avgRangeReduction: this.average(rows.map((row: any) => row.visual.rangeReductionPercent)),
            totalCountDelta: countDeltas.reduce((sum: number, value: number) => sum + value, 0),
            avgMmdSdDelta: this.average(mmdSdDeltas),
            avgCspDelta: this.average(cspDeltas),
        };
    }

    public sampleCase(sample: any, caseId: string) {
        return sample?.cases?.find((row: any) => row.caseId === caseId) || null;
    }

    public async selectSample(sampleId: string) {
        this.selectedSampleId = sampleId;
        await this.service.render();
    }

    public sampleActiveClass(sampleId: string) {
        if (this.selectedSampleId === sampleId) {
            return "flex items-center justify-between gap-3 rounded-lg border border-emerald-500 bg-emerald-50 px-3 py-2 text-left text-[13px] font-semibold text-emerald-800";
        }
        return "flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-[13px] font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50";
    }

    public deltaClass(value: any) {
        if (value > 0) return "text-blue-700";
        if (value < 0) return "text-red-600";
        return "text-gray-500";
    }

    public signed(value: any, digits: number = 0) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
        const numberValue = Number(value);
        const prefix = numberValue > 0 ? "+" : "";
        return `${prefix}${this.format(numberValue, digits)}`;
    }

    public format(value: any, digits: number = 0) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return "-";
        return Number(value).toLocaleString("ko-KR", {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        });
    }

    public metricValue(row: any, key: string) {
        const value = row?.metrics?.[key];
        if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
        return Number(value);
    }

    public comparisonValue(row: any, caseRow: any) {
        if (!row || !caseRow) return null;
        if (row.source === "metrics") return this.metricValue(caseRow, row.key);
        return caseRow[row.key];
    }

    public comparisonDelta(row: any) {
        const baseValue = this.comparisonValue(row, this.originalCase);
        const targetValue = this.comparisonValue(row, this.backgroundCase);
        if (baseValue === null || baseValue === undefined || targetValue === null || targetValue === undefined) return null;
        return Number(targetValue) - Number(baseValue);
    }

    public comparisonDeltaPercent(row: any) {
        const baseValue = this.comparisonValue(row, this.originalCase);
        const delta = this.comparisonDelta(row);
        if (!baseValue || delta === null || delta === undefined) return null;
        return (delta / Number(baseValue)) * 100;
    }

    public heatmapImage(key: string) {
        const fullKey = `${key}Full`;
        const downloadKey = `${key}Download`;
        return this.imageRef(
            this.selectedBackgroundVisual?.[key],
            this.selectedBackgroundVisual?.[fullKey],
            this.selectedBackgroundVisual?.[downloadKey],
            this.selectedBackgroundVisual?.nativeWidth || this.selectedSample?.width,
            this.selectedBackgroundVisual?.nativeHeight || this.selectedSample?.height,
            this.isTrustedNative(),
        );
    }

    public caseImage(row: any) {
        const fullImage = row?.fullImage || (row?.caseId === "original" ? this.selectedSample?.originalFullImage : "");
        const downloadImage = row?.downloadImage || (row?.caseId === "original" ? this.selectedSample?.originalDownloadImage : "");
        return this.imageRef(
            row?.preview,
            fullImage,
            downloadImage,
            fullImage ? (row?.nativeWidth || this.selectedSample?.width) : null,
            fullImage ? (row?.nativeHeight || this.selectedSample?.height) : null,
            this.isTrustedNative(),
        );
    }

    private imageRef(preview: string, fullImage: string = "", downloadImage: string = "", nativeWidth: any = null, nativeHeight: any = null, trustedNative: boolean = false) {
        const resolvedPreview = preview || fullImage || downloadImage || "";
        const useNative = trustedNative && !!fullImage && !!nativeWidth && !!nativeHeight;
        const resolvedFull = useNative ? fullImage : resolvedPreview;
        const resolvedDownload = useNative ? (downloadImage || resolvedFull) : "";
        return {
            preview: resolvedPreview,
            fullImage: resolvedFull,
            downloadImage: resolvedDownload,
            nativeWidth: useNative ? nativeWidth : null,
            nativeHeight: useNative ? nativeHeight : null,
            nativeTrusted: useNative,
        };
    }

    private resolveImageInput(input: any) {
        if (typeof input === "string") return this.imageRef(input);
        return this.imageRef(
            input?.preview || input?.src || input?.source || "",
            input?.fullImage || input?.full || input?.sourceFull || "",
            input?.downloadImage || input?.download || input?.sourceDownload || "",
            input?.nativeWidth || null,
            input?.nativeHeight || null,
            input?.nativeTrusted === true,
        );
    }

    private modalFileName(title: string, src: string) {
        const ext = (src || "").split("?")[0].split(".").pop() || "jpg";
        const safeTitle = (title || "cellytics-image")
            .replace(/[^0-9A-Za-z._-]+/g, "_")
            .replace(/^_+|_+$/g, "") || "cellytics-image";
        return safeTitle.endsWith(`.${ext}`) ? safeTitle : `${safeTitle}.${ext}`;
    }

    public async openImageModal(input: any, title: string, caption: string = "") {
        const image = this.resolveImageInput(input);
        const src = image.fullImage || image.preview;
        this.imageModal = {
            src,
            preview: image.preview,
            download: image.downloadImage || "",
            filename: this.modalFileName(title, src),
            nativeWidth: image.nativeWidth,
            nativeHeight: image.nativeHeight,
            usingNative: !!image.nativeWidth && !!image.nativeHeight,
            title,
            caption,
        };
        await this.service.render();
    }

    public async closeImageModal() {
        this.imageModal = null;
        await this.service.render();
    }

    private average(values: any[]) {
        const numbers = values.map((value: any) => Number(value)).filter((value: number) => !Number.isNaN(value));
        if (!numbers.length) return null;
        return numbers.reduce((sum: number, value: number) => sum + value, 0) / numbers.length;
    }
}

export default PageBackgroundComponent;